'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/store/hooks'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdScience } from 'react-icons/md'
import {
    getLabIntegration,
    createLabIntegration,
    updateLabIntegration,
    regenerateWebhookSecret,
    CreateLabIntegrationData,
    LabIntegrationProtocol,
    LAB_PROTOCOL_LABELS,
} from '@/lib/api/health-integrations/lab-integration-api'

export default function LabIntegrationFormPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [regenerating, setRegenerating] = useState(false)

    const [formData, setFormData] = useState<CreateLabIntegrationData>({
        name: '',
        protocol: 'HL7_V2',
        host: '',
        port: undefined,
        apiUrl: '',
        apiKey: '',
        active: true,
        autoProcess: true,
        autoMatch: true,
        settings: {},
    })

    const [webhookUrl, setWebhookUrl] = useState('')
    const [webhookSecret, setWebhookSecret] = useState('')

    useEffect(() => {
        if (isEditing && id) {
            loadIntegration(parseInt(id))
        }
    }, [id])

    const loadIntegration = async (integrationId: number) => {
        setLoading(true)
        try {
            const response = await getLabIntegration(integrationId)
            if (response.success) {
                const integration = response.result
                setFormData({
                    name: integration.name,
                    protocol: integration.protocol,
                    host: integration.host || '',
                    port: integration.port,
                    apiUrl: integration.apiUrl || '',
                    apiKey: '', // Don't load the actual key
                    active: integration.active,
                    autoProcess: integration.autoProcess,
                    autoMatch: integration.autoMatch,
                    settings: integration.settings || {},
                })
                setWebhookUrl(integration.webhookUrl || '')
                setWebhookSecret(integration.webhookSecret || '')
            } else {
                toast.error(response.error || 'Erro ao carregar integração')
                navigate('/health-integrations/lab')
            }
        } catch (error) {
            toast.error('Erro ao carregar integração')
            navigate('/health-integrations/lab')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name.trim()) {
            toast.error('Informe o nome da integração')
            return
        }

        setSaving(true)
        try {
            const data = { ...formData }
            if (!data.apiKey) {
                delete (data as any).apiKey
            }
            if (!data.port) {
                delete (data as any).port
            }

            const response = isEditing
                ? await updateLabIntegration(parseInt(id!), data)
                : await createLabIntegration(data)

            if (response.success) {
                toast.success(
                    `Integração ${isEditing ? 'atualizada' : 'criada'} com sucesso`
                )
                navigate('/health-integrations/lab')
            } else {
                toast.error(
                    response.error ||
                    `Erro ao ${isEditing ? 'atualizar' : 'criar'} integração`
                )
            }
        } catch (error) {
            toast.error(
                `Erro ao ${isEditing ? 'atualizar' : 'criar'} integração`
            )
        } finally {
            setSaving(false)
        }
    }

    const handleRegenerateSecret = async () => {
        if (!id) return

        setRegenerating(true)
        try {
            const response = await regenerateWebhookSecret(parseInt(id))
            if (response.success) {
                setWebhookUrl(response.result.webhookUrl || '')
                setWebhookSecret(response.result.webhookSecret || '')
                toast.success('Segredo do webhook regenerado')
            } else {
                toast.error(response.error || 'Erro ao regenerar segredo')
            }
        } catch (error) {
            toast.error('Erro ao regenerar segredo')
        } finally {
            setRegenerating(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando integração...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/health-integrations/lab')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                        <MdScience className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar' : 'Nova'} Integração
                            Laboratorial
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados da integração'
                                : 'Configure uma nova integração com laboratório'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Informações Básicas
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Nome *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="Ex: Laboratorio XYZ"
                                value={formData.name}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        name: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Protocolo *</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={formData.protocol}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        protocol: e.target
                                            .value as LabIntegrationProtocol,
                                    })
                                }
                            >
                                {Object.entries(LAB_PROTOCOL_LABELS).map(
                                    ([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-4 mt-4">
                        <label className="label cursor-pointer gap-2">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-secondary"
                                checked={formData.active}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        active: e.target.checked,
                                    })
                                }
                            />
                            <span className="label-text">Ativa</span>
                        </label>

                        <label className="label cursor-pointer gap-2">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-secondary"
                                checked={formData.autoProcess}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        autoProcess: e.target.checked,
                                    })
                                }
                            />
                            <span className="label-text">
                                Processar automaticamente
                            </span>
                        </label>

                        <label className="label cursor-pointer gap-2">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-secondary"
                                checked={formData.autoMatch}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        autoMatch: e.target.checked,
                                    })
                                }
                            />
                            <span className="label-text">
                                Vincular paciente automaticamente
                            </span>
                        </label>
                    </div>
                </div>

                {formData.protocol === 'HL7_V2' && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold mb-4">
                            Configuração MLLP (HL7)
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Host</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    placeholder="Ex: 192.168.1.100"
                                    value={formData.host}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            host: e.target.value,
                                        })
                                    }
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Porta</span>
                                </label>
                                <input
                                    type="number"
                                    className="input input-bordered"
                                    placeholder="Ex: 2575"
                                    value={formData.port || ''}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            port: e.target.value
                                                ? parseInt(e.target.value)
                                                : undefined,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}

                {(formData.protocol === 'FHIR_R4' ||
                    formData.protocol === 'CUSTOM') && (
                        <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                            <h2 className="text-lg font-semibold mb-4">
                                Configuração API
                            </h2>
                            <div className="grid grid-cols-1 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">URL da API</span>
                                    </label>
                                    <input
                                        type="url"
                                        className="input input-bordered"
                                        placeholder="Ex: https://api.laboratorio.com/fhir"
                                        value={formData.apiUrl}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                apiUrl: e.target.value,
                                            })
                                        }
                                    />
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">
                                            Chave da API
                                            {isEditing && ' (deixe vazio para manter)'}
                                        </span>
                                    </label>
                                    <input
                                        type="password"
                                        className="input input-bordered"
                                        placeholder="Chave de autenticação"
                                        value={formData.apiKey}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                apiKey: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                {isEditing && webhookUrl && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold mb-4">
                            Webhook (Recebimento de Resultados)
                        </h2>
                        <div className="grid grid-cols-1 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">URL do Webhook</span>
                                </label>
                                <div className="join w-full">
                                    <input
                                        type="text"
                                        className="input input-bordered join-item flex-1"
                                        value={webhookUrl}
                                        readOnly
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-ghost join-item"
                                        onClick={() => {
                                            navigator.clipboard.writeText(
                                                webhookUrl
                                            )
                                            toast.success('URL copiada')
                                        }}
                                    >
                                        Copiar
                                    </button>
                                </div>
                                <label className="label">
                                    <span className="label-text-alt">
                                        Configure esta URL no sistema do laboratório
                                        para receber resultados
                                    </span>
                                </label>
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">
                                        Segredo do Webhook
                                    </span>
                                </label>
                                <div className="join w-full">
                                    <input
                                        type="text"
                                        className="input input-bordered join-item flex-1"
                                        value={webhookSecret ? '********' : ''}
                                        readOnly
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-warning join-item"
                                        onClick={handleRegenerateSecret}
                                        disabled={regenerating}
                                    >
                                        {regenerating ? (
                                            <span className="loading loading-spinner loading-sm"></span>
                                        ) : (
                                            'Regenerar'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() =>
                            navigate('/health-integrations/lab')
                        }
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Salvar' : 'Criar'}
                    </button>
                </div>
            </form>
        </div>
    )
}
